import { Controller } from "@hotwired/stimulus"

const schemas = {
  chapters: { label: "Chapter", maximum: 200, fields: { title: "string", start_seconds: "number" } },
  resources: { label: "Resource", maximum: 100, fields: { title: "string", url: "string" } },
  caption_tracks: { label: "Caption track", maximum: 30, fields: { label: "string", language: "string", url: "string", default: "boolean" } }
}

export function parseTimestamp(value) {
  const text = value.trim()
  if (!/^\d+(?:\.\d+)?$/.test(text) && !/^\d+:\d{2}(?::\d{2})?(?:\.\d+)?$/.test(text)) return null
  const parts = text.split(":").map(Number)
  if (parts.length > 1 && parts.at(-1) >= 60) return null
  if (parts.length === 3 && parts[1] >= 60) return null
  const seconds = parts.reduce((total, part) => total * 60 + part, 0)
  return Number.isFinite(seconds) && seconds >= 0 && seconds <= 604800 ? seconds : null
}

export function formatTimestamp(value) {
  if (!Number.isFinite(value) || value < 0 || value > 604800) return String(value ?? "")
  const rounded = Math.round(value * 1000000) / 1000000
  const whole = Math.floor(rounded)
  const fraction = rounded.toFixed(6).split(".")[1].replace(/0+$/, "")
  const seconds = String(whole % 60).padStart(2, "0") + (fraction ? `.${fraction}` : "")
  return whole >= 3600 ? `${Math.floor(whole / 3600)}:${String(Math.floor(whole / 60) % 60).padStart(2, "0")}:${seconds}` : `${Math.floor(whole / 60)}:${seconds}`
}

export default class extends Controller {
  static targets = ["editor", "rows", "raw", "json", "template", "message", "apply", "add"]
  static values = { kind: String }

  connect() {
    this.schema = schemas[this.kindValue]
    if (!this.schema) return
    this.applyTarget.hidden = false
    this.load()
  }

  load() {
    let rows
    try {
      rows = JSON.parse(this.jsonTarget.value.trim() || "[]")
      if (!Array.isArray(rows) || rows.length > this.schema.maximum || rows.some(row => !this.editable(row))) throw new Error()
    } catch {
      this.showJson()
      this.messageTarget.textContent = "Correct the JSON list to use the row editor. Your entered JSON has been kept."
      return
    }
    this.rowsTarget.replaceChildren()
    rows.forEach(row => this.appendRow(row))
    this.rawTarget.hidden = true
    this.editorTarget.hidden = false
    this.editorTarget.disabled = false
    this.messageTarget.textContent = ""
    this.renumber()
  }

  editable(row) {
    return row && typeof row === "object" && !Array.isArray(row) && Object.entries(row).every(([key, value]) =>
      this.schema.fields[key] && typeof value === this.schema.fields[key] && (typeof value !== "number" || Number.isFinite(value)))
  }

  showJson() {
    this.rawTarget.hidden = false
    this.editorTarget.hidden = true
    this.editorTarget.disabled = true
  }

  appendRow(values = {}) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    row.querySelectorAll("[data-field]").forEach(input => {
      const value = values[input.dataset.field]
      if (input.type === "checkbox") input.checked = value === true
      else input.value = input.dataset.field === "start_seconds" && typeof value === "number" ? formatTimestamp(value) : value ?? ""
    })
    this.rowsTarget.append(row)
    return row
  }

  add() {
    if (this.rows.length >= this.schema.maximum) return
    const row = this.appendRow()
    this.sync()
    row.querySelector("input")?.focus()
  }

  remove(event) {
    const row = event.currentTarget.closest("[data-video-metadata-row]")
    const focusRow = row.nextElementSibling || row.previousElementSibling
    row.remove()
    this.sync()
    ;(focusRow?.querySelector("input") || this.addTarget).focus()
  }

  move(event) {
    const row = event.currentTarget.closest("[data-video-metadata-row]")
    const direction = event.currentTarget.dataset.direction
    if (direction === "up" && row.previousElementSibling) row.previousElementSibling.before(row)
    if (direction === "down" && row.nextElementSibling) row.nextElementSibling.after(row)
    this.sync()
    event.currentTarget.focus()
  }

  sync(event) {
    if (this.editorTarget.disabled) return
    const changed = event?.target
    if (changed?.dataset.field === "default" && changed.checked) {
      this.rowsTarget.querySelectorAll('[data-field="default"]').forEach(input => { if (input !== changed) input.checked = false })
    }
    const rows = this.rows.map(row => Object.fromEntries(Array.from(row.querySelectorAll("[data-field]")).map(input => {
      let value = input.type === "checkbox" ? input.checked : input.value
      if (input.dataset.field === "start_seconds") {
        const seconds = parseTimestamp(input.value)
        const error = seconds === null ? "Enter seconds, mm:ss or hh:mm:ss (up to 7 days)." : ""
        input.setCustomValidity(error)
        const feedback = row.querySelector("[data-time-error]")
        if (feedback) feedback.textContent = error
        value = seconds === null ? input.value : seconds
      }
      return [input.dataset.field, value]
    })))
    this.jsonTarget.value = JSON.stringify(rows, null, 2)
    this.renumber()
    if (this.kindValue === "chapters") {
      const invalidTime = rows.some(row => typeof row.start_seconds !== "number")
      const invalidOrder = rows.some((row, index) => typeof row.start_seconds === "number" && index > 0 && typeof rows[index - 1].start_seconds === "number" && row.start_seconds <= rows[index - 1].start_seconds)
      this.messageTarget.textContent = invalidTime ? "Correct the highlighted chapter time." : invalidOrder ? "Chapter times must increase from one row to the next." : ""
    }
  }

  renumber() {
    this.rows.forEach((row, index) => {
      row.querySelector("[data-row-label]").textContent = `${this.schema.label} ${index + 1}`
      row.querySelector('[data-direction="up"]').disabled = index === 0
      row.querySelector('[data-direction="down"]').disabled = index === this.rows.length - 1
    })
    this.addTarget.disabled = this.rows.length >= this.schema.maximum
  }

  get rows() {
    return Array.from(this.rowsTarget.children)
  }
}
